// =============================================================================
// Create Terminal Modal - 插件面板专用的紧凑终端创建对话框
// =============================================================================

import React, { useState, useEffect } from 'react';
import { X, Terminal, Settings, Zap, Play } from 'lucide-react';
import { apiClient } from '../services/apiClient';
import LoadingSpinner from './LoadingSpinner';

const CreateTerminalModal = ({ isOpen, onClose, onSuccess, workspaceId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [terminalTypes, setTerminalTypes] = useState([]);
  const [scriptTemplates, setScriptTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState('general');
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptParameters, setScriptParameters] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    shell: '/bin/bash',
    cols: 80,
    rows: 24
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadTerminalTypes();
      loadScriptTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedType) {
      loadScriptTemplates();
    }
  }, [selectedType]);

  const loadTerminalTypes = async () => {
    try {
      const response = await apiClient.get('/terminals/types/info');
      if (response.data.success) {
        setTerminalTypes(response.data.data.types);
      }
    } catch (error) {
      console.error('Failed to load terminal types:', error);
      // 提供默认类型
      setTerminalTypes([
        { id: 'general', name: '通用终端', description: '适用于一般命令和操作' },
        { id: 'service', name: '服务终端', description: '运行长期服务，如Web服务器、数据库等' },
        { id: 'ai_tool', name: 'AI工具', description: 'Claude CLI等AI助手工具' },
        { id: 'development', name: '开发终端', description: '代码构建、测试、Git操作等开发任务' },
        { id: 'monitoring', name: '监控终端', description: '日志监控、系统监控等' },
        { id: 'temporary', name: '临时终端', description: '快速查看、一次性任务' }
      ]);
    }
  };

  const loadScriptTemplates = async () => {
    try {
      const response = await apiClient.get('/scripts/templates');
      if (response.data.success) {
        const allTemplates = response.data.data.templates;
        // 过滤出适合当前终端类型的脚本
        const filteredTemplates = allTemplates.filter(template => 
          template.terminalType === selectedType || template.terminalType === 'general'
        );
        setScriptTemplates(filteredTemplates);
      }
    } catch (error) {
      console.error('Failed to load script templates:', error);
      setScriptTemplates([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScriptParameterChange = (paramName, value) => {
    setScriptParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsLoading(true);
    setError('');
    
    try {
      const terminalConfig = {
        workspaceId,
        terminalType: selectedType,
        name: formData.name || undefined,
        shell: formData.shell,
        cols: formData.cols,
        rows: formData.rows,
        autoExecuteScript: selectedScript?.id,
        scriptParameters: selectedScript ? scriptParameters : undefined
      };

      const response = await apiClient.post('/terminals', terminalConfig);
      
      if (response.data.success) {
        onSuccess(response.data.data);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to create terminal:', error);
      setError(error.response?.data?.error || 'Failed to create terminal');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', shell: '/bin/bash', cols: 80, rows: 24 });
    setSelectedType('general');
    setSelectedScript(null);
    setScriptParameters({});
    setShowAdvanced(false);
    setError('');
    onClose();
  };

  const getTypeIcon = (typeId) => {
    switch (typeId) {
      case 'service': return '🚀';
      case 'ai_tool': return '🤖';
      case 'development': return '🔧';
      case 'monitoring': return '📊';
      case 'temporary': return '📋';
      default: return '💻';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'service': return '🚀';
      case 'monitoring': return '📊';
      case 'development': return '🔧';
      default: return '📋';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Terminal className="w-5 h-5" />
            创建终端
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-accent rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* 终端类型选择 */}
          <div>
            <label className="block text-sm font-medium mb-2">终端类型</label>
            <div className="grid grid-cols-2 gap-2">
              {terminalTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setSelectedType(type.id)}
                  className={`p-2 border rounded text-left text-xs transition-all ${
                    selectedType === type.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span>{getTypeIcon(type.id)}</span>
                    <span className="font-medium">{type.name}</span>
                  </div>
                  <p className="text-muted-foreground line-clamp-2">
                    {type.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* 脚本选择 */}
          {scriptTemplates.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-1">
                <Zap className="w-4 h-4" />
                自动执行脚本 (可选)
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setSelectedScript(null)}
                  className={`w-full p-2 border rounded text-left text-xs ${
                    !selectedScript ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <span className="font-medium">无脚本</span>
                  <p className="text-muted-foreground">手动操作终端</p>
                </button>
                {scriptTemplates.slice(0, 3).map((script) => (
                  <button
                    key={script.id}
                    type="button"
                    onClick={() => setSelectedScript(script)}
                    className={`w-full p-2 border rounded text-left text-xs ${
                      selectedScript?.id === script.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span>{getCategoryIcon(script.category)}</span>
                      <span className="font-medium">{script.name}</span>
                      {script.autoExecute && <Zap className="w-3 h-3 text-yellow-500" />}
                    </div>
                    <p className="text-muted-foreground line-clamp-2">
                      {script.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 脚本参数 */}
          {selectedScript && selectedScript.parameters && Object.keys(selectedScript.parameters).length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">脚本参数</label>
              <div className="space-y-2">
                {Object.entries(selectedScript.parameters).map(([paramName, paramConfig]) => (
                  <div key={paramName}>
                    <label className="block text-xs text-muted-foreground mb-1">
                      {paramConfig.description || paramName}
                    </label>
                    {paramConfig.type === 'boolean' ? (
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={scriptParameters[paramName] || paramConfig.default || false}
                          onChange={(e) => handleScriptParameterChange(paramName, e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-xs">启用</span>
                      </label>
                    ) : (
                      <input
                        type={paramConfig.type === 'number' ? 'number' : 'text'}
                        value={scriptParameters[paramName] || paramConfig.default || ''}
                        onChange={(e) => handleScriptParameterChange(paramName, e.target.value)}
                        placeholder={paramConfig.default}
                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 基本设置 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              终端名称 (可选)
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="留空将自动生成"
              className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* 高级设置 */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <Settings className="w-4 h-4" />
              高级设置
              <span className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {showAdvanced && (
              <div className="mt-2 p-3 bg-accent/20 rounded space-y-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Shell</label>
                  <select
                    name="shell"
                    value={formData.shell}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1 text-xs border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="/bin/bash">Bash</option>
                    <option value="/bin/sh">Shell</option>
                    <option value="/bin/zsh">Zsh</option>
                    <option value="/bin/fish">Fish</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">列数</label>
                    <input
                      type="number"
                      name="cols"
                      value={formData.cols}
                      onChange={handleInputChange}
                      min="20"
                      max="200"
                      className="w-full px-2 py-1 text-xs border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">行数</label>
                    <input
                      type="number"
                      name="rows"
                      value={formData.rows}
                      onChange={handleInputChange}
                      min="10"
                      max="100"
                      className="w-full px-2 py-1 text-xs border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-sm border border-border rounded hover:bg-accent"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  创建中...
                </>
              ) : (
                <>
                  <Play className="w-3 h-3" />
                  创建终端
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTerminalModal;
